// src/pages/LihtcIntakePage.js
// UNPUBLISHED (unlinked) LIHTC utility allowance INTAKE.
//
// Two modes, one action each. "One property" walks the 26 CFR 1.42-10 questions
// and submits it (POST /lihtc/intake); "Portfolio CSV" submits many at once.
// Neither computes anything — the schedules are produced separately against
// tariffs sourced live for each town at the time they are built.
//
// The town is free text, not a dropdown of the localities we already hold
// tariffs for: rates are sourced per request, so a property anywhere can be
// submitted. Towns already on file are offered as a datalist, nothing more.
//
// Styling is deliberately identical to /utility-allowances/join: same token set,
// same sticky frosted header with the back pill, same 680px column, same Field /
// TextInput focus behaviour, same pill button. It should read as one product.
//
// Route: /utility-allowances/lihtc — not linked from anywhere.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Brand from '../components/Brand';
import LihtcCsvUpload from '../components/LihtcCsvUpload';

const COMPLIANCE_API =
  process.env.REACT_APP_COMPLIANCE_API ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:3200'
    : 'https://housing-compliance-service.onrender.com');

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

const T = {
  blue600: '#1d4ed8', // blue-700: reads at 4.5:1 on the blue-100 chips (WCAG 1.4.3),
  blue50: '#eff6ff',
  blue100: '#dbeafe',
  gray900: '#111827',
  gray800: '#1f2937',
  gray600: '#4b5563',
  gray500: '#5f6673', // darkened for 4.5:1 on light backgrounds (WCAG 1.4.3),
  gray400: '#6b7280', // darkened for 4.5:1 on white (WCAG 1.4.3),
  gray200: '#e5e7eb',
  gray100: '#f3f4f6',
  white: '#ffffff',
  radius: '16px',
  radiusSm: '12px',
  radiusPill: '999px',
  shadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
  shadowSm: '0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05)',
  gradient: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #faf5ff 100%)',
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const inputStyle = {
  width: '100%',
  padding: '0.7rem 0.9rem',
  fontSize: '0.92rem',
  border: `1px solid ${T.gray200}`,
  borderRadius: T.radiusSm,
  outline: 'none',
  fontFamily: T.font,
  color: T.gray900,
  background: T.white,
  boxSizing: 'border-box',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.82rem',
  fontWeight: 600,
  color: T.gray800,
  marginBottom: '0.35rem',
};

const focusOn = (e) => { e.target.style.borderColor = T.blue600; e.target.style.boxShadow = `0 0 0 3px ${T.blue50}`; };
const focusOff = (e) => { e.target.style.borderColor = T.gray200; e.target.style.boxShadow = 'none'; };

function Field({ label, required, hint, children }) {
  // Give a single form control an id and point the label at it (WCAG 1.3.1, 3.3.2);
  // groups such as YesNo keep their own per-option labels.
  const autoId = React.useId();
  // A group (YesNo, chips) ignores the id it is given, so its own option labels still apply.
  // Comments and whitespace around the control are not elements, so look through them.
  const kids = React.Children.toArray(children);
  const isControl = (k) => React.isValidElement(k) && (typeof k.type === 'string'
    ? ['input', 'select', 'textarea'].includes(k.type)
    : ('value' in k.props || 'onChange' in k.props || 'type' in k.props || 'rows' in k.props));
  const controls = kids.filter(isControl);
  const single = controls.length === 1 ? controls[0] : null;
  const id = single ? (single.props.id || autoId) : undefined;
  const control = single && !single.props.id ? kids.map(k => (k === single ? React.cloneElement(k, { id }) : k)) : children;
  return (
    <div>
      <label htmlFor={id} style={labelStyle}>
        {label} {required && <span style={{ color: T.blue600 }}>*</span>}
      </label>
      {control}
      {hint && <p style={{ margin: '0.35rem 0 0', fontSize: '0.78rem', color: T.gray500, lineHeight: 1.5 }}>{hint}</p>}
    </div>
  );
}

function TextInput(props) {
  return <input {...props} style={inputStyle} onFocus={focusOn} onBlur={focusOff} />;
}

function SelectInput({ children, ...props }) {
  return <select {...props} style={inputStyle} onFocus={focusOn} onBlur={focusOff}>{children}</select>;
}

function Chip({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: '0.4rem 0.85rem',
      fontSize: '0.84rem',
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: T.font,
      borderRadius: T.radiusPill,
      border: `1px solid ${active ? T.blue600 : T.gray200}`,
      background: active ? T.blue100 : T.white,
      color: active ? T.blue600 : T.gray600,
      transition: 'all 0.15s ease',
    }}>{children}</button>
  );
}

const STRUCTURES = [
  { idx: 1, label: 'Manufactured Home (Mobile Home)' },
  { idx: 2, label: 'Single Family Detached (House)' },
  { idx: 3, label: 'Single Family Attached (Duplex/Rowhouse/Townhouse)' },
  { idx: 4, label: 'Low-Rise Apt (2-4 unit Walk-up)' },
  { idx: 5, label: 'High-Rise Apt (5+ unit Bldg)' },
];

// Trash is included: 1.42-10(a) excludes only telephone, cable TV and internet.
const SERVICES = [
  { key: 'heating', label: 'Heating' },
  { key: 'waterHeating', label: 'Water Heating' },
  { key: 'cooking', label: 'Cooking' },
  { key: 'otherElectric', label: 'Other Electric (lights, plugs)' },
  { key: 'ac', label: 'Air Conditioning' },
  { key: 'waterSewer', label: 'Water & Sewer' },
  { key: 'trash', label: 'Trash Collection' },
];

const PAYERS = [
  { v: 'tenant', label: 'Tenant pays direct' },
  { v: 'submetered', label: 'Sub-metered' },
  { v: 'owner', label: 'Owner pays' },
];

const FUELS = {
  heatingFuel: { label: 'Heating fuel', when: 'heating', options: [
    ['gas', 'Natural Gas'], ['electric', 'Electric Resistance'], ['heatpump', 'Electric Heat Pump'], ['oil', 'Fuel Oil'], ['propane', 'Bottle Gas (Propane)'],
  ]},
  waterHeatFuel: { label: 'Water heating fuel', when: 'waterHeating', options: [
    ['gas', 'Natural Gas'], ['electric', 'Electric'], ['oil', 'Fuel Oil'], ['propane', 'Bottle Gas (Propane)'],
  ]},
  cookingFuel: { label: 'Cooking fuel', when: 'cooking', options: [
    ['gas', 'Natural Gas'], ['electric', 'Electric'], ['propane', 'Bottle Gas (Propane)'],
  ]},
};

const METHODS = [
  ['husm', 'HUD Utility Schedule Model — computed here'],
  ['pha', 'Applicable PHA utility allowance'],
  ['utility_estimate', 'Written local utility company estimate'],
  ['energy_model', 'Energy consumption model (licensed engineer / qualified professional)'],
  ['agency', 'State allocating agency estimate'],
];

const BED_CHOICES = [0, 1, 2, 3, 4, 5];
const bedLabel = (b) => (b === 0 ? 'Studio' : `${b} BR`);
const isTenantBorne = (v) => v === 'tenant' || v === 'submetered';

export default function LihtcIntakePage() {
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const [localities, setLocalities] = useState([]);
  const [form, setForm] = useState({
    town: '', state: 'MA', propertyName: '', address: '', bin: '', placedInService: '', units: '',
    assistance: '', method: '', hasVoucherHolders: false,
    structureIdx: '', bedrooms: [],
    services: { heating: '', waterHeating: '', cooking: '', otherElectric: '', ac: '', waterSewer: '', trash: '' },
    equipment: { heatingFuel: '', waterHeatFuel: '', cookingFuel: '' },
    trashMonthly: '', noticeDate: '', maxGrossRent: {},
    contactName: '', contactEmail: '', contactOrg: '', notes: '',
  });
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('single');
  const [queuing, setQueuing] = useState(false);
  const [queued, setQueued] = useState(null);

  useEffect(() => {
    fetch(`${COMPLIANCE_API}/api/utility-allowance/lihtc/localities`)
      .then((r) => r.json())
      .then((b) => setLocalities(b.localities || []))
      .catch(() => setLocalities([]));
  }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setService = (key, val) => setForm((f) => ({ ...f, services: { ...f.services, [key]: val } }));
  const setFuel = (key, val) => setForm((f) => ({ ...f, equipment: { ...f.equipment, [key]: val } }));
  const setMgr = (bed, val) => setForm((f) => ({ ...f, maxGrossRent: { ...f.maxGrossRent, [bed]: val } }));
  const toggleBed = (b) => setForm((f) => ({
    ...f,
    bedrooms: f.bedrooms.includes(b) ? f.bedrooms.filter((x) => x !== b) : [...f.bedrooms, b].sort((a, z) => a - z),
  }));

  // The method gate is answerable before anything else is filled in, so a
  // property that can't use HUSM at all finds out before doing the whole form.
  const gateBlocks = form.assistance === 'rd' || form.assistance === 'hud'
    || (form.assistance === 'none' && form.method && form.method !== 'husm');

  // The only submit on the page. Nothing is computed in the browser: the
  // property is recorded so the current tariffs for its town can be sourced and
  // the schedule built against them. /lihtc/preview still exists for pricing a
  // property internally against rates already on file, but it is not a button
  // here — a draft priced off possibly-stale rates is not the deliverable.
  const saveToQueue = async () => {
    setError(null); setQueued(null);
    if (!form.assistance) { setError('Answer whether the building receives RD or HUD rental assistance.'); return; }
    if (form.assistance === 'none' && !form.method) { setError('Pick which permitted method this property uses.'); return; }
    if (!form.propertyName.trim()) { setError('Enter the property name.'); return; }
    if (!form.town.trim()) { setError('Enter the town or city — the tariffs get sourced for that locality.'); return; }
    if (!gateBlocks) {
      if (!form.structureIdx) { setError('Pick the building type.'); return; }
      if (!form.bedrooms.length) { setError('Pick at least one bedroom size.'); return; }
      const unanswered = SERVICES.filter((s2) => !form.services[s2.key]);
      if (unanswered.length) { setError(`Answer who pays for: ${unanswered.map((s2) => s2.label).join(', ')}.`); return; }
      const missingFuels = Object.entries(FUELS)
        .filter(([key, f]) => isTenantBorne(form.services[f.when]) && !form.equipment[key]);
      if (missingFuels.length) {
        const names = missingFuels.map(([, f]) => f.label.toLowerCase());
        const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
        setError(`Pick the ${list}.`);
        return;
      }
    }
    if (!form.contactEmail.trim()) { setError('Enter an email so we can send the schedule.'); return; }
    setQueuing(true);
    const mgr = {};
    for (const b of form.bedrooms) {
      const v = Number(form.maxGrossRent[b]);
      if (Number.isFinite(v) && v > 0) mgr[String(b)] = v;
    }
    try {
      const r = await fetch(`${COMPLIANCE_API}/api/utility-allowance/lihtc/intake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          propertyName: form.propertyName.trim(),
          town: form.town.trim(), state: form.state.trim() || 'MA',
          address: form.address.trim() || undefined, bin: form.bin.trim() || undefined,
          placedInService: form.placedInService || undefined,
          units: form.units ? Number(form.units) : undefined,
          assistance: form.assistance, method: form.method,
          hasVoucherHolders: form.hasVoucherHolders,
          structureIdx: form.structureIdx, bedrooms: form.bedrooms,
          services: form.services, equipment: form.equipment,
          trashMonthly: form.trashMonthly ? Number(form.trashMonthly) : undefined,
          noticeDate: form.noticeDate || undefined,
          maxGrossRent: Object.keys(mgr).length ? mgr : undefined,
          contactName: form.contactName.trim() || undefined,
          contactEmail: form.contactEmail.trim(),
          contactOrg: form.contactOrg.trim() || undefined,
          notes: form.notes.trim() || undefined,
        }),
      });
      const body = await r.json();
      if (!r.ok) throw new Error((body.messages && body.messages.join('; ')) || body.message || 'Could not send the request.');
      setQueued(body.saved);
    } catch (e) {
      setError(e.message);
    } finally {
      setQueuing(false);
    }
  };

  const card = {
    background: T.white,
    border: `1px solid ${T.gray200}`,
    borderRadius: T.radius,
    boxShadow: T.shadowSm,
    padding: mobile ? '1.1rem 1rem' : '1.5rem 1.75rem',
    marginBottom: mobile ? '1.25rem' : '1.75rem',
  };
  const formCard = { ...card, boxShadow: T.shadow, padding: mobile ? '1.25rem 1rem' : '2rem', display: 'flex', flexDirection: 'column', gap: '1.1rem' };
  const h3 = { fontSize: '0.95rem', fontWeight: 700, color: T.gray900, margin: 0, borderBottom: `1px solid ${T.gray100}`, paddingBottom: '0.5rem' };
  const grid = { display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: mobile ? '0.9rem' : '1rem' };
  const note = { fontSize: '0.8rem', color: T.gray500, lineHeight: 1.55, margin: 0 };

  return (
    <div style={{ fontFamily: T.font, minHeight: '100vh', background: T.gradient, display: 'flex', flexDirection: 'column' }}>
      {/* Header — sticky frosted, centered branding, back pill pinned left */}
      <header style={{
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${T.gray200}`,
        padding: mobile ? '0.75rem 1rem' : '1.25rem 1.5rem',
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          justifyContent: mobile ? 'flex-start' : 'center',
          maxWidth: '1400px', margin: '0 auto', position: 'relative',
        }}>
          <div style={mobile ? { flexShrink: 0 } : { position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)' }}>
            <button
              onClick={() => navigate('/utility-allowances')}
              style={{
                background: T.gray100, border: `1px solid ${T.gray200}`, borderRadius: T.radiusPill,
                cursor: 'pointer', fontSize: '0.85rem', color: T.gray600, padding: '0.45rem 0.85rem',
                transition: 'all 0.15s ease', fontFamily: T.font, fontWeight: 600,
              }}
              title="Back to schedules"
              onMouseOver={e => { e.currentTarget.style.background = T.blue50; e.currentTarget.style.borderColor = T.blue600; e.currentTarget.style.color = T.blue600; }}
              onMouseOut={e => { e.currentTarget.style.background = T.gray100; e.currentTarget.style.borderColor = T.gray200; e.currentTarget.style.color = T.gray600; }}
            >
              ←
            </button>
          </div>
          <Brand as="a" href="https://gethousingai.com" target="_blank" rel="noopener noreferrer" theme="light" markClassName="text-blue-600"
            style={{ fontWeight: 700, color: T.gray900, fontSize: mobile ? '1.1rem' : '1.35rem', fontFamily: T.font, textDecoration: 'none' }} />
          <span style={{ color: '#d1d5db', fontSize: '1.5rem', fontWeight: 300 }}>|</span>
          <span style={{
            fontSize: mobile ? '0.72rem' : '0.85rem',
            padding: mobile ? '0.2rem 0.55rem' : '0.25rem 0.75rem',
            background: T.blue100, color: T.blue600, borderRadius: T.radiusPill, fontWeight: 600,
          }}>
            LIHTC · Utility Allowances
          </span>
        </div>
      </header>

      <main style={{ padding: mobile ? '1.25rem 0.75rem' : '2.5rem 1.5rem', flex: 1 }}>
        <div style={{ maxWidth: '680px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: mobile ? '1.25rem' : '2rem' }}>
            <h1 style={{
              fontSize: mobile ? '1.35rem' : '1.9rem', fontWeight: 800, color: T.gray900,
              marginBottom: '0.6rem', letterSpacing: '-0.02em',
            }}>
              Get Your LIHTC Utility Allowance
            </h1>
            <p style={{ color: T.gray600, fontSize: mobile ? '0.84rem' : '0.92rem', lineHeight: 1.6, maxWidth: '520px', margin: '0 auto' }}>
              Tell us about the property and we&rsquo;ll build a per-property allowance under
              26 CFR &sect;&nbsp;1.42-10, computed from the HUD Utility Schedule Model at your
              locality&rsquo;s climate. We source the current tariffs for your town at the time we
              build it and cite every rate to its filing. We check which method the building is
              even permitted to use first, and the schedule carries the 90-day notice and annual
              recompute dates.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: mobile ? '1.25rem' : '1.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Chip active={mode === 'single'} onClick={() => setMode('single')}>One property</Chip>
            <Chip active={mode === 'csv'} onClick={() => setMode('csv')}>Portfolio CSV</Chip>
          </div>

          {mode === 'csv' && (
            <LihtcCsvUpload api={COMPLIANCE_API} T={T} card={card} h3={h3} mobile={mobile} />
          )}

          {mode === 'single' && (<>
            {/* 1 — Method eligibility, first, because it can end the form */}
            <div style={formCard}>
              <h3 style={h3}>Which method is this building allowed to use?</h3>
              <p style={note}>
                &sect;&nbsp;1.42-10(b) decides this before the owner gets a choice. An RD-assisted building uses the
                RD allowance; a building with HUD rental assistance uses the applicable HUD allowance. Only if
                neither applies does the owner pick from the five options.
              </p>
              <Field label="Does the building receive RD or HUD rental assistance?" required>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', paddingTop: '0.15rem' }}>
                  <Chip active={form.assistance === 'rd'} onClick={() => set({ assistance: 'rd' })}>Rural Development assisted</Chip>
                  <Chip active={form.assistance === 'hud'} onClick={() => set({ assistance: 'hud' })}>HUD rental assistance</Chip>
                  <Chip active={form.assistance === 'none'} onClick={() => set({ assistance: 'none' })}>Neither</Chip>
                </div>
              </Field>
              {form.assistance === 'none' && (
                <Field label="Method elected for this property" required>
                  <SelectInput value={form.method} onChange={(e) => set({ method: e.target.value })}>
                    <option value="" disabled>Select a method…</option>
                    {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </SelectInput>
                </Field>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: T.gray800, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.hasVoucherHolders}
                  onChange={(e) => set({ hasVoucherHolders: e.target.checked })}
                  style={{ accentColor: T.blue600, width: 16, height: 16 }} />
                Some units house Section 8 voucher holders
              </label>

              {gateBlocks && (
                <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: T.radiusSm, padding: '0.8rem 1rem', fontSize: '0.86rem', color: '#92400e', lineHeight: 1.6 }}>
                  {form.assistance === 'rd' && (
                    <>This building receives Rural Development assistance, so the RD-prescribed allowance governs the whole
                    building &mdash; the HUD Utility Schedule Model is not an available option. <b>26 CFR &sect;&nbsp;1.42-10(b)(1)</b>.</>
                  )}
                  {form.assistance === 'hud' && (
                    <>This building receives HUD rental assistance, so the applicable HUD allowance governs &mdash; the HUD
                    Utility Schedule Model is not an available option. <b>26 CFR &sect;&nbsp;1.42-10(b)(2)</b>.</>
                  )}
                  {form.assistance === 'none' && form.method && form.method !== 'husm' && (
                    <>That allowance comes from the source you elected, and the HUD Utility Schedule Model can&rsquo;t stand
                    in for it. <b>26 CFR &sect;&nbsp;1.42-10(b)(4)(ii)</b>.</>
                  )}
                  {' '}Send it through anyway if you&rsquo;d like us to look at it &mdash; we&rsquo;ll tell you what applies.
                </div>
              )}
            </div>

            {!gateBlocks && (
              <>
                {/* 2 — Property */}
                <div style={formCard}>
                  <h3 style={h3}>Property</h3>
                  <div style={grid}>
                    <Field label="Town / city" required>
                      <TextInput type="text" list="lihtc-localities" value={form.town}
                        placeholder="e.g. Salem" onChange={(e) => set({ town: e.target.value })} />
                      <datalist id="lihtc-localities">
                        {localities.map((o) => <option key={o.slug} value={o.label.split(',')[0]} />)}
                      </datalist>
                    </Field>
                    <Field label="State" required>
                      <TextInput type="text" maxLength={2} value={form.state}
                        onChange={(e) => set({ state: e.target.value.toUpperCase() })} />
                    </Field>
                    <Field label="Property name" required>
                      <TextInput type="text" value={form.propertyName} placeholder="e.g. Derby Lofts" onChange={(e) => set({ propertyName: e.target.value })} />
                    </Field>
                    <Field label="Address">
                      <TextInput type="text" value={form.address} placeholder="Street, City" onChange={(e) => set({ address: e.target.value })} />
                    </Field>
                    <Field label="BIN">
                      <TextInput type="text" value={form.bin} placeholder="e.g. MA-26-00123" onChange={(e) => set({ bin: e.target.value })} />
                    </Field>
                    <Field label="Placed in service">
                      <TextInput type="date" value={form.placedInService} onChange={(e) => set({ placedInService: e.target.value })} />
                    </Field>
                    <Field label="Total units">
                      <TextInput type="number" min="1" value={form.units} onChange={(e) => set({ units: e.target.value })} />
                    </Field>
                  </div>
                  <Field label="Building type" required>
                    <SelectInput value={form.structureIdx} onChange={(e) => set({ structureIdx: Number(e.target.value) })}>
                      <option value="" disabled>Select a building type…</option>
                      {STRUCTURES.map((s) => <option key={s.idx} value={s.idx}>{s.label}</option>)}
                    </SelectInput>
                  </Field>
                  <Field label="Bedroom sizes at this property" required>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', paddingTop: '0.15rem' }}>
                      {BED_CHOICES.map((b) => (
                        <Chip key={b} active={form.bedrooms.includes(b)} onClick={() => toggleBed(b)}>{bedLabel(b)}</Chip>
                      ))}
                    </div>
                  </Field>
                </div>

                {/* 3 — Who pays */}
                <div style={formCard}>
                  <h3 style={h3}>Who pays for each service?</h3>
                  <p style={note}>
                    An allowance exists only for utilities the tenant pays directly. Sub-metered service counts as
                    tenant-paid under &sect;&nbsp;1.42-10(e). Owner-paid service is in the rent and carries no allowance.
                    Telephone, cable television and internet are excluded from gross rent entirely and are not asked about.
                  </p>
                  <div>
                    {SERVICES.map((s) => (
                      <div key={s.key} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem',
                        padding: '0.6rem 0', borderTop: `1px solid ${T.gray100}`, flexWrap: 'wrap',
                      }}>
                        <span style={{ fontSize: '0.9rem', color: T.gray800, fontWeight: 500 }}>{s.label}</span>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          {PAYERS.map((p) => (
                            <Chip key={p.v} active={form.services[s.key] === p.v} onClick={() => setService(s.key, p.v)}>{p.label}</Chip>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {isTenantBorne(form.services.trash) && (
                    <Field
                      label="Tenant's monthly trash charge ($)"
                      hint="Trash counts toward gross rent when the tenant pays it, but the HUD Utility Schedule Model doesn't produce a trash figure and no trash rate is on file. Leave it blank and no trash row is estimated — the schedule will say so."
                    >
                      <TextInput type="number" min="0" step="0.01" value={form.trashMonthly}
                        placeholder="from the hauler or municipal bill" onChange={(e) => set({ trashMonthly: e.target.value })} />
                    </Field>
                  )}
                </div>

                {/* 4 — Equipment */}
                <div style={formCard}>
                  <h3 style={h3}>Equipment</h3>
                  {['heating', 'waterHeating', 'cooking'].some((k) => isTenantBorne(form.services[k])) ? (
                    <div style={grid}>
                      {Object.entries(FUELS).map(([key, f]) => (
                        isTenantBorne(form.services[f.when]) ? (
                          <Field key={key} label={f.label} required>
                            <SelectInput value={form.equipment[key]} onChange={(e) => setFuel(key, e.target.value)}>
                              <option value="" disabled>Select a fuel…</option>
                              {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </SelectInput>
                          </Field>
                        ) : null
                      ))}
                    </div>
                  ) : ['heating', 'waterHeating', 'cooking'].every((k) => form.services[k] === 'owner') ? (
                    <p style={note}>No fuel details needed — heating, water heating and cooking are all owner-paid.</p>
                  ) : (
                    <p style={note}>Answer the section above first — fuel questions appear for each service the tenant bears.</p>
                  )}
                </div>

                {/* 5 — Optional gross rent + timing */}
                <div style={formCard}>
                  <h3 style={h3}>Gross rent check &amp; notice date</h3>
                  <p style={note}>
                    Optional. Enter the maximum gross rent your Agency publishes for this set-aside and the schedule
                    will show the rent still collectible after the allowance. Leave blank to skip the check &mdash; no limits are assumed.
                  </p>
                  {form.bedrooms.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: '0.75rem' }}>
                      {form.bedrooms.map((b) => (
                        <Field key={b} label={`${bedLabel(b)} max gross rent`}>
                          <TextInput type="number" min="0" value={form.maxGrossRent[b] || ''} onChange={(e) => setMgr(b, e.target.value)} />
                        </Field>
                      ))}
                    </div>
                  ) : (
                    <p style={note}>Pick bedroom sizes above first.</p>
                  )}
                  <Field label="Date made available to tenants & Agency"
                    hint="Defaults to today. The 90-day period under § 1.42-10(c)(2) runs from this date.">
                    <TextInput type="date" value={form.noticeDate} onChange={(e) => set({ noticeDate: e.target.value })} />
                  </Field>
                </div>
              </>
            )}

            <div style={formCard}>
              <h3 style={h3}>Where to send it</h3>
              <div style={grid}>
                <Field label="Your name">
                  <TextInput type="text" value={form.contactName} onChange={(e) => set({ contactName: e.target.value })} />
                </Field>
                <Field label="Email" required>
                  <TextInput type="email" value={form.contactEmail} placeholder="you@example.com" onChange={(e) => set({ contactEmail: e.target.value })} />
                </Field>
              </div>
              <Field label="Owner / management company">
                <TextInput type="text" value={form.contactOrg} onChange={(e) => set({ contactOrg: e.target.value })} />
              </Field>
              <Field label="Anything else we should know?">
                <textarea rows={3} value={form.notes} onChange={(e) => set({ notes: e.target.value })}
                  placeholder="Portfolio size, timing, which agency allocates, questions…"
                  style={{ ...inputStyle, resize: 'vertical' }} onFocus={focusOn} onBlur={focusOff} />
              </Field>
            </div>

            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: T.radiusSm, padding: '0.8rem 1rem', fontSize: '0.86rem', marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            <button type="button" onClick={saveToQueue} disabled={queuing} style={{
              width: '100%', padding: '0.85rem', background: T.blue600, color: T.white, border: 'none',
              borderRadius: T.radiusPill, fontWeight: 700, fontSize: '0.95rem', fontFamily: T.font,
              cursor: queuing ? 'not-allowed' : 'pointer', opacity: queuing ? 0.7 : 1,
              transition: 'background 0.2s ease', marginBottom: '0.75rem',
            }}
              onMouseOver={e => { if (!queuing) e.currentTarget.style.background = '#1d4ed8'; }}
              onMouseOut={e => { e.currentTarget.style.background = T.blue600; }}
            >
              {queuing ? 'Sending…' : 'Get My LIHTC Schedule'}
            </button>
            <p style={{ ...note, textAlign: 'center', marginBottom: mobile ? '1.25rem' : '1.75rem' }}>
              We source the current tariffs for {form.town.trim() || 'your locality'} and build the schedule from the
              HUD Utility Schedule Model, with every rate cited to its filing. Nothing is computed from stale rates.
            </p>

            {queued && (
              <div style={{ ...card, borderLeft: `4px solid ${T.blue600}` }}>
                <h3 style={h3}>Request received</h3>
                <p style={{ fontSize: '0.9rem', color: T.gray800, margin: '0.85rem 0 0.5rem', lineHeight: 1.65 }}>
                  {form.propertyName.trim() || 'Your property'} is in the queue as <b>#{queued.id}</b>.
                </p>
                <p style={note}>
                  {queued.rates_on_file
                    ? `Cited tariffs are already on file for ${queued.town || 'this locality'}, so the schedule is quick to turn around.`
                    : `We don't have tariffs on file for ${queued.town || 'this locality'} yet — we'll source them from the current filings before building the schedule.`}
                </p>
              </div>
            )}

          </>)}
        </div>
      </main>

      <footer style={{ background: T.gray900, color: T.white, padding: mobile ? '1.25rem 0' : '2rem 0', fontFamily: T.font }}>
        <div style={{
          maxWidth: '1200px', margin: '0 auto', padding: '0 1rem',
          display: 'flex', flexDirection: mobile ? 'column' : 'row',
          flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center',
          gap: mobile ? '0.5rem' : '1rem',
        }}>
          <Brand theme="dark" markClassName="text-blue-600" style={{ fontWeight: 600, fontSize: '0.95rem' }} />
          <div style={{ fontSize: mobile ? '0.78rem' : '0.875rem', color: '#d1d5db' }}>
            © 2026 HousingAI. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
